import { Editor, Transforms, Range } from 'vendor/slate'
import { v4 as uuidv4 } from 'uuid'
import { markRangeAsRemove, insertTextAsSuggestion, isRemovedSuggestionLeaf } from './transforms'

export const withSuggestions = (editor, { currentUserId = null } = {}) => {
  const { insertText, deleteFragment, deleteBackward, deleteForward } = editor

  editor.isSuggesting = false
  editor.activeSuggestionId = null
  editor.suggestionUserId = currentUserId

  // Assigned by the React layer (editor.js) so this plugin stays UI-agnostic.
  // Defaults to a no-op until wired so leaf click handlers are always safe.
  if (typeof editor.openSuggestion !== 'function') {
    editor.openSuggestion = () => {}
  }

  editor.setSuggesting = value => {
    editor.isSuggesting = !!value
    if (!editor.isSuggesting) {
      editor.activeSuggestionId = null
    }
  }

  editor.ensureActiveSuggestionId = () => {
    if (!editor.activeSuggestionId) {
      editor.activeSuggestionId = uuidv4()
    }
    return editor.activeSuggestionId
  }

  const applyRemoveToRange = (range, { collapseEdge = 'start' } = {}) => {
    const suggestionId = editor.ensureActiveSuggestionId()
    const userId = editor.suggestionUserId

    markRangeAsRemove(editor, range, suggestionId, userId)
    Transforms.collapse(editor, { edge: collapseEdge })
  }

  const leafAtPoint = point => {
    if (!point) return null
    try {
      const [node] = Editor.leaf(editor, point.path)
      return node
    } catch {
      return null
    }
  }

  editor.insertText = (text, options) => {
    if (!editor.isSuggesting || !text) {
      return insertText(text, options)
    }

    const { selection } = editor
    if (!selection) return insertText(text, options)

    Editor.withoutNormalizing(editor, () => {
      if (!Range.isCollapsed(selection)) {
        applyRemoveToRange(selection, { collapseEdge: 'end' })
      }

      const suggestionId = editor.ensureActiveSuggestionId()
      insertTextAsSuggestion(editor, text, suggestionId, editor.suggestionUserId)
    })
  }

  editor.deleteFragment = direction => {
    if (!editor.isSuggesting) {
      return deleteFragment(direction)
    }

    const { selection } = editor
    if (!selection || Range.isCollapsed(selection)) {
      return deleteFragment(direction)
    }

    Editor.withoutNormalizing(editor, () => {
      if (direction === 'backward') {
        Transforms.collapse(editor, { edge: 'end' })
        applyRemoveToRange(selection)
      } else {
        Transforms.collapse(editor, { edge: 'start' })
        applyRemoveToRange(selection)
      }
    })
  }

  editor.deleteBackward = unit => {
    if (!editor.isSuggesting) {
      return deleteBackward(unit)
    }

    const { selection } = editor
    if (!selection || !Range.isCollapsed(selection)) {
      return deleteBackward(unit)
    }

    const before = Editor.before(editor, selection, { unit: unit || 'character' })
    if (!before) return

    // If the char we'd "delete" is already a pending removal, just move the
    // cursor across it instead of re-marking, so backspace keeps progressing.
    if (isRemovedSuggestionLeaf(leafAtPoint(before))) {
      Transforms.select(editor, before)
      return
    }

    const range = { anchor: before, focus: selection.anchor }
    Editor.withoutNormalizing(editor, () => {
      applyRemoveToRange(range, { collapseEdge: 'start' })
    })
  }

  editor.deleteForward = unit => {
    if (!editor.isSuggesting) {
      return deleteForward(unit)
    }

    const { selection } = editor
    if (!selection || !Range.isCollapsed(selection)) {
      return deleteForward(unit)
    }

    const after = Editor.after(editor, selection, { unit: unit || 'character' })
    if (!after) return

    // Inspect the leaf covering the character ahead of the cursor (the one we'd
    // remove). If it's already a pending removal, step over it instead.
    const forwardLeaf = leafAtPoint(after) || leafAtPoint(selection.anchor)
    if (isRemovedSuggestionLeaf(forwardLeaf)) {
      Transforms.select(editor, after)
      return
    }

    const range = { anchor: selection.anchor, focus: after }
    Editor.withoutNormalizing(editor, () => {
      applyRemoveToRange(range, { collapseEdge: 'end' })
    })
  }

  return editor
}

export {
  acceptSuggestionById,
  rejectSuggestionById,
  applyAIEditSuggestion,
  applyAIReplaceSelection,
  acceptAllSuggestions,
  rejectAllSuggestions,
  findSuggestionIds,
  getSuggestionIdFromLeaf,
  getSuggestionTypeFromLeaf
} from './transforms'

export { SUGGESTION_ROOT, getSuggestionKey, SUGGESTION_TYPES } from './constants'
